import { Box, Grid, LinearProgress } from '@mui/material';
import { useEffect, useState } from 'react';
import useWidth from '../../../../components/Hooks/useWidth';
import LangEnum from '../../../../lang/LangEnum';
import { useTranslation } from '../../../translation/TranslationContext';
import { useSignatureContext } from '../contexts/signature.context';
import { SignatureContextType } from '../contexts/signature.context.types';
import { signedContractRepository } from '../repository/signedContract.repository';
import { ModalContractPage } from '../types';
import OpenPdfFeedback from './OpenPdfFeedback';

type Props = {
  orderId?: string;
};

const newContract = async (orderId: string): Promise<string> => {
  const response = await signedContractRepository().generateContractPdf(orderId);
  if (response.ok) {
    return response.data! as string;
  }
  throw Error('Error to build new contract');
};

const getExistent = async (contractId: string, readOnly?: boolean): Promise<string> => {
  const response = await signedContractRepository().getExistent(contractId, readOnly);
  if (response.ok) {
    return response.data! as string;
  }
  throw Error('Error to get existent signed contract');
};

const getContract = (
  orderId: string,
  page: SignatureContextType.Page,
  selectedSignedContract: SignatureContextType.SelectedSignedContract,
): Promise<string> => {
  const isReadOnly = ModalContractPage.READ_CONTRACT_ONLY === page;
  const isReadAndSign = ModalContractPage.READ_AND_SIGN_CONTRACT === page;
  const isReadAndSignNewContract = ModalContractPage.READ_AND_SIGN_NEW_CONTRACT === page;

  if (isReadOnly || isReadAndSign) {
    return getExistent(selectedSignedContract?.id!, isReadOnly);
  }

  if (isReadAndSignNewContract) {
    return newContract(orderId);
  }

  throw Error(`wrong page type: ${page}`);
};

const getIsIOSMobileDevice = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
};

export const ReadContract = ({ orderId }: Props) => {
  const {
    contractUrl,
    loadContractUrl,
    isLoading,
    isLoadingToFalse,
    selectedSignedContract,
    page,
  } = useSignatureContext();
  const { mediumDownWidth } = useWidth();
  const [title, setTitle] = useState<string>('');
  const { translate } = useTranslation();
  const isIOS = getIsIOSMobileDevice();

  const getContractTitle = async () => {
    if (selectedSignedContract) {
      setTitle(
        translate(LangEnum.ORDER_CONTRACT_HISTORY_TABLE_CELL_CONTRACT_TITLE, {
          code: selectedSignedContract?.order.code,
          version: selectedSignedContract?.version,
        }),
      );
      return;
    }
    const response = await signedContractRepository().getContractTitle(orderId!);
    if (response.ok) {
      const newTitle = response.data as string;
      setTitle(newTitle);
    }
  };

  useEffect(() => {
    if (orderId && !contractUrl) {
      getContract(orderId, page, selectedSignedContract)
        .then(loadContractUrl)
        .then(getContractTitle)
        .then(isLoadingToFalse);
    }

    getContractTitle();
    isLoadingToFalse();
  }, []);

  return (
    <Box
      style={{ overflow: 'hidden' }}
      sx={{
        height: '100%',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          display: isLoading ? 'block' : 'none',
        }}
      >
        <LinearProgress />
      </Box>
      {!isLoading && contractUrl && (
        <Grid
          container
          spacing={1}
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            right: 0,
            left: 0,
            display: 'flow',
          }}
        >
          <Grid style={{ height: '100%' }} size={12}>
            {!isIOS && <embed src={contractUrl} title={title} height="100%" width="100%" />}
            {mediumDownWidth && isIOS && <OpenPdfFeedback src={contractUrl} />}
          </Grid>
        </Grid>
      )}
    </Box>
  );
};
